import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { apiClient } from '../services/api';
import type { SupportTicket } from '../types';
import './SupportPage.css';

export const SupportTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const data = await apiClient.getSupportTickets();
        setTickets(data);
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Error al cargar los tickets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return `Hace ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container support-page">
        <PageHeader title="Historial de tickets" />
        <div className="support-loading">
          <div className="support-loading-spinner"></div>
          <p className="support-loading-text">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container support-page">
        <PageHeader title="Historial de tickets" />
        <div className="tickets-empty">
          <div className="tickets-empty-icon">⚠️</div>
          <h3 className="tickets-empty-title">{error}</h3>
          <button className="support-submit-btn" onClick={() => window.location.reload()}>
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container support-page">
      <PageHeader title="История обращений" />

      <div className="support-content">
        {tickets.length === 0 ? (
          <div className="tickets-empty">
            <div className="tickets-empty-icon">💬</div>
            <h3 className="tickets-empty-title">Sin tickets</h3>
            <p className="tickets-empty-text">
              Si tienes una pregunta o problema, crea un nuevo ticket
            </p>
            <button className="support-submit-btn" onClick={() => navigate('/profile/support')}>
              Crear ticket
            </button>
          </div>
        ) : (
          <div className="tickets-list">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="ticket-card"
                onClick={() => navigate(`/profile/support/tickets/${ticket.id}`)}
              >
                <div className="ticket-header">
                  <h3 className="ticket-subject">{ticket.subject}</h3>
                  <span className={`ticket-status ${ticket.status}`}>
                    {ticket.status === 'open' ? 'Abierto' : 'Cerrado'}
                  </span>
                </div>
                
                {ticket.last_message && (
                  <p className="ticket-preview">
                    <span className={`sender ${ticket.last_message.is_admin ? 'admin' : ''}`}>
                      {ticket.last_message.is_admin ? 'Soporte: ' : 'Tú: '}
                    </span>
                    {ticket.last_message.message}
                  </p>
                )}

                <div className="ticket-meta">
                  <span className="ticket-date">{formatDate(ticket.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

